import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, minsToHhMm, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyVidsDashboard } from './companyVidsApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  Chip,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  paletteAt
} from '../../../../component/common/designSystem';
import { WATCH_LENGTH_RAMP, VIEWER_DEPTH_RAMP, PROGRESS_RAMP, kindChipClass } from './vidsTheme';

/**
 * Shoorah Videos → Dashboard.
 *
 * Covers both video libraries at once — Health Videos (the NHS/guide videos
 * embedded from Vimeo) and Shoorah Vids (uploaded in the admin panel) — because
 * they share a collection and a company admin reads them as one thing. The
 * "Library Split" panel is where the two are pulled apart.
 *
 * Everything comes from the per-user watch records, so it answers one question
 * in layers: are your people watching (reach, views, time), how far do they get
 * (completion rate, watch length), how broadly (videos per viewer), when
 * (trend, weekday) and what (top videos, experts, themes).
 *
 * A note on "views": the watch record holds one row per person per video, so a
 * view is one person watching one video, not one play. Someone who returns to
 * the same video still counts once — which is why completion rate, not view
 * count, is the honest engagement number here.
 *
 * `fetcher` is a prop so the super-admin Vids Hub can render this page
 * unscoped; the builders behind both are the same.
 */
const VidsDashboard = ({ fetcher = fetchCompanyVidsDashboard, populationLabel = 'users' }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const mostWatched = stats?.mostWatched || [];
  const viewsTrend = stats?.viewsTrend || [];
  const watchMinsTrend = stats?.watchMinsTrend || [];
  const weekdayViews = stats?.weekdayViews || [];
  const watchLengths = stats?.watchLengthDistribution || [];
  const viewerDepth = stats?.viewerDepth || [];
  const progressSplit = stats?.progressSplit || [];
  const kindBreakdown = stats?.kindBreakdown || [];
  const topExperts = stats?.topExperts || [];
  const topCategories = stats?.topCategories || [];

  const topTenViews = mostWatched.reduce((sum, item) => sum + (item.viewers || 0), 0);

  // What share of the population ever opened a video.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.viewers / stats.totalUsers) * 100) : null;

  // Colour-coded per video so the bar chart and the breakdown table below read
  // as the same ranking.
  const barOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: mostWatched.map((item, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '65%', distributed: true }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => formatBigNumber(val),
      style: { fontSize: '12px' }
    },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: {
      categories: mostWatched.map((item) => item.name),
      labels: { formatter: (val) => formatBigNumber(val) }
    },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} viewers` } }
  };

  /**
   * Views and watch minutes on one chart, two axes: a spike in views that isn't
   * matched by minutes means people are opening videos and dropping out, which
   * is the opposite conclusion from the views line alone.
   *
   * Dated by each record's last watch, so this is recent activity rather than a
   * replay of every session ever.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 4] },
    fill: { type: ['gradient', 'solid'], gradient: AREA_FILL.gradient },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(viewsTrend.length), tickAmount: 6 },
    yaxis: [
      { seriesName: 'Views', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Watch time',
        opposite: true,
        labels: { formatter: (val) => minsToHhMm(val) }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} views` },
        { formatter: (val) => minsToHhMm(val) }
      ]
    }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayViews.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} views` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Most Watched"
          value={stats?.mostWatchedVideo || '-'}
        />
        <StatCard
          icon={STAT_ICONS.plays}
          title="Total Views"
          value={stats ? formatBigNumber(stats.totalViews) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Total Watch Time"
          value={stats ? minsToHhMm(stats.totalWatchTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title="Viewers"
          value={stats ? formatBigNumber(stats.viewers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Completion Rate"
          value={stats ? `${stats.completionRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Avg Watch Time"
          value={stats ? `${stats.avgWatchMins} min` : '-'}
        />
      </div>

      {/* ---------- Library split ----------
          The one panel where the two kinds are separated: Health Videos come
          from Vimeo, Shoorah Vids are uploaded in the admin panel. Both rows
          are always present, so an unused library reads as a zero rather than
          disappearing. */}
      <Panel
        title="Health Videos vs Shoorah Vids"
        subtitle="How your two video libraries are being used"
        className="mt-6"
      >
        {kindBreakdown.length > 0 ? (
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Library</th>
                <th className="py-2 pr-4 font-medium text-right">Videos</th>
                <th className="py-2 pr-4 font-medium text-right">Views</th>
                <th className="py-2 pr-4 font-medium text-right">Share of views</th>
                <th className="py-2 pr-4 font-medium text-right">Completed</th>
                <th className="py-2 pr-4 font-medium text-right">Completion rate</th>
                <th className="py-2 font-medium text-right">Watch Time</th>
              </>
            }
          >
            {kindBreakdown.map((row) => (
              <tr key={row.kind} className={PANEL_ROW_CLASS}>
                <td className="py-2 pr-4">
                  <Chip className={kindChipClass(row.kind)}>{row.kind}</Chip>
                </td>
                <td className="py-2 pr-4 text-right">{row.videos.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right">{row.views.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right">{row.sharePercentage}%</td>
                <td className="py-2 pr-4 text-right">{row.completions.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right">{row.completionRate}%</td>
                <td className="py-2 text-right">{minsToHhMm(row.watchMins)}</td>
              </tr>
            ))}
          </PanelTable>
        ) : (
          <PanelEmpty>No video activity yet.</PanelEmpty>
        )}
      </Panel>

      {/* ---------- Breakdowns ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="How Far They Get"
          subtitle="Progress per video watched"
          data={progressSplit}
          colors={PROGRESS_RAMP}
          unit="views"
        />
        <MiniDonutCard
          title="Watch Length"
          subtitle="Minutes watched per video"
          data={watchLengths}
          colors={WATCH_LENGTH_RAMP}
          unit="views"
        />
        <MiniDonutCard
          title="Viewer Depth"
          subtitle="Videos opened per viewer"
          data={viewerDepth}
          colors={VIEWER_DEPTH_RAMP}
          unit="viewers"
        />
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Most Watched Videos" subtitle={`Top 10 among your ${populationLabel}`}>
          {mostWatched.length > 0 ? (
            <Chart
              type="bar"
              height={380}
              width="100%"
              series={[{ name: 'Viewers', data: mostWatched.map((item) => item.viewers) }]}
              options={barOptions}
            />
          ) : (
            <PanelEmpty>No video activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Views & Watch Time" subtitle="Per day — last 30 days">
          {viewsTrend.length > 0 ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Views', type: 'area', data: viewsTrend },
                { name: 'Watch time', type: 'line', data: watchMinsTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No views recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Reach ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Video Reach"
          subtitle={`Share of ${populationLabel} who watched a video`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.viewers)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        <Panel title="Views by Weekday" subtitle="When people sit down to watch">
          {weekdayViews.some((day) => day.views > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Views', data: weekdayViews.map((day) => day.views) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No views recorded yet.</PanelEmpty>
          )}
        </Panel>

        {/* Expert-authored videos carry a name; Shoorah's own content is grouped
            under "Shoorah", so this reads as "who your people watch". */}
        <Panel title="Most Watched Experts" subtitle="Views by video author">
          <ProgressList items={topExperts} unit="views" />
        </Panel>
      </div>

      {/* ---------- Themes and the ranking ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        {/* A video can belong to several categories, so these totals overlap
            and never sum to the view count — a ranked list, not a donut. */}
        <Panel title="Top Themes" subtitle="Views by video category">
          <ProgressList items={topCategories} unit="views" />
        </Panel>

        {mostWatched.length > 0 && (
          <Panel
            title="Top Videos Breakdown"
            subtitle={
              stats?.topTenSharePercentage
                ? `These ${mostWatched.length} account for ${stats.topTenSharePercentage}% of all views`
                : 'Viewers, completions and watch time per video'
            }
            className="xl:col-span-2"
          >
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">#</th>
                  <th className="py-2 pr-4 font-medium">Video</th>
                  <th className="py-2 pr-4 font-medium">Library</th>
                  <th className="py-2 pr-4 font-medium text-right">Viewers</th>
                  <th className="py-2 pr-4 font-medium text-right">Share of top 10</th>
                  <th className="py-2 pr-4 font-medium text-right">Completed</th>
                  <th className="py-2 font-medium text-right">Watch Time</th>
                </>
              }
            >
              {mostWatched.map((item, index) => (
                <tr key={item.id || index} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">
                    {/* Rank swatch ties each row to its bar in the chart above. */}
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: paletteAt(index) }}
                      />
                      {index + 1}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{item.name}</td>
                  <td className="py-2 pr-4">
                    <Chip className={kindChipClass(item.kind)}>{item.kind}</Chip>
                  </td>
                  <td className="py-2 pr-4 text-right">{item.viewers.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right">
                    {topTenViews > 0 ? `${Math.round((item.viewers / topTenViews) * 100)}%` : '-'}
                  </td>
                  <td className="py-2 pr-4 text-right">{item.completions.toLocaleString()}</td>
                  <td className="py-2 text-right">{minsToHhMm(item.watchMins)}</td>
                </tr>
              ))}
            </PanelTable>
          </Panel>
        )}
      </div>
    </div>
  );
};

VidsDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default VidsDashboard;
